import FlatList from "common/Lists/FlatList";
import {
  useAssetsCardsAndSorters,
  useAssetsData,
} from "features/portfolio/common/hooks";
import CoinCard from "features/portfolio/Assets/CoinCard";
import { RootState } from "app/store";
import { PrecisionConfig } from "types";
import EmptyView from "common/Empty/EmptyView";
import { portfolioFilters } from "@shared/slices/filters.slice";
import { EmptyStatesType } from "common/Icons/types";
import {
  ConsolidatedCoinData,
  ConsolidatedCoinDataWithKey,
  LanguageNameSpaces,
} from "@shared/types";
import React, { useCallback, useContext } from "react";
import { selectConsolidatedPortfolioData } from "@shared/slices/portfolio.slice.ts";
import { PageWrapperContext } from "common/Home/PageWrapper/PageWrapper.context.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useTheme } from "styled-components";
import { NewLookSortersRow } from "features/portfolio/NewLook/styles.ts";
import { NEW_LOOK_COIN_ROW_SORTERS_PADDING } from "features/portfolio/NewLook/NewLookCoinCard.tsx";

interface MobileAssetsProps {
  renderTopContainer: () => React.JSX.Element;
  precisionSelector: (state: RootState) => PrecisionConfig | undefined;
  loading?: boolean;
  refreshing?: boolean;
  onRefresh?: () => void;
  dataSelector?: (state: any) => ConsolidatedCoinData[];
}

const MobileAssets = ({
  renderTopContainer: propsRenderTopContainer,
  precisionSelector,
  loading,
  refreshing,
  onRefresh,
  dataSelector: propsDataSelector,
}: MobileAssetsProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const { search } = useContext(PageWrapperContext);
  const { isNewLook } = useTheme();

  const dataSelector = propsDataSelector ?? selectConsolidatedPortfolioData;
  const { filteredCoinsData: coins } = useAssetsData({
    dataSelector,
    searchFilter: search.value,
  });
  const { sortedData: coinsSortedData, cardsSorters: coinsCardsSorters } =
    useAssetsCardsAndSorters({ data: coins });

  const emptyState =
    !coinsSortedData?.length && !loading ? (
      <EmptyView
        iconKey={EmptyStatesType.Coins}
        title={t("assets.empty_title")}
        iconWidth={100}
        titleStyle={{ maxWidth: 300 }}
        objectName="coins"
        isSearch={!!search.value?.length}
        filtersSlice={portfolioFilters}
      />
    ) : undefined;

  const renderContent = ({ item }: { item: ConsolidatedCoinDataWithKey }) => {
    return <CoinCard coinData={item} precisionSelector={precisionSelector} />;
  };

  const renderTopContainer = useCallback(() => {
    return (
      <div style={{ marginBottom: 8 }}>
        {propsRenderTopContainer()}
        {isNewLook
          ? !!coinsSortedData?.length && (
              <NewLookSortersRow
                $padding={NEW_LOOK_COIN_ROW_SORTERS_PADDING}
                $endAligned={true}
              >
                {coinsCardsSorters}
              </NewLookSortersRow>
            )
          : coinsCardsSorters}
      </div>
    );
  }, [
    propsRenderTopContainer,
    coinsCardsSorters,
    isNewLook,
    coinsSortedData?.length,
  ]);

  return (
    <FlatList
      data={coinsSortedData || []}
      loading={loading}
      renderContent={renderContent}
      renderTopContainer={renderTopContainer}
      gap={12}
      refreshing={refreshing}
      onRefresh={onRefresh}
      hasActionButton={true}
      wrapperStyle={WRAPPER_STYLE}
      emptyState={emptyState}
    />
  );
};

export default React.memo(MobileAssets);

const WRAPPER_STYLE = { padding: 16 };
